import { Radar, CheckCircle2, PackageCheck, MinusCircle, FileText } from "lucide-react";
import { inr, fmtDateTime, authedFileUrl } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

const STEPS = [
  { key: "manifested", label: "Manifest created", match: ["manifest", "created"] },
  { key: "pickup_scheduled", label: "Pickup scheduled", match: ["pickup scheduled", "pickup_created"] },
  { key: "picked_up", label: "Picked up by courier", match: ["picked", "pickup_complete", "pickup complete"] },
  { key: "in_transit", label: "In transit", match: ["in transit", "in_transit", "received", "shipment"] },
  { key: "out_for_delivery", label: "Out for delivery", match: ["out for delivery", "out_for_delivery", "ofd"] },
  { key: "delivered", label: "Delivered", match: ["delivered"] },
];

const normalize = (s) => String(s || "").toLowerCase().replace(/-/g, "_");

const currentStepIndex = (status) => {
  const s = normalize(status);
  if (s === "delivered") return 5;
  if (s === "out_for_delivery") return 4;
  if (s === "in_transit" || s === "reached_hub") return 3;
  if (s === "picked_up" || s === "pickup_completed" || s === "pickup_complete") return 2;
  if (s === "pickup_scheduled" || s === "pickup_pending") return 1;
  return 0;
};

const eventMatches = (ev, step) => {
  const txt = `${ev.status || ""} ${ev.description || ""}`.toLowerCase();
  return step.match.some((m) => txt.includes(m));
};

export const TrackingDetail = ({ detail, publicView = false }) => {
  if (!detail) return null;
  const events = detail.events || [];
  const status = normalize(detail.status);
  const isTerminalBad = status === "rto" || status === "ndr" || status === "cancelled";
  const idx = currentStepIndex(detail.status);
  const statusLabel = String(detail.status || "").replace(/[_-]/g, " ").toUpperCase();

  const steps = STEPS.map((step, i) => ({
    ...step,
    event: events.find((e) => eventMatches(e, step)),
    state: i < idx ? "done" : i === idx ? "current" : "pending",
  }));

  const pods = (detail.pods || []).filter(
    (p) => !p.expired && !(p.expires_at && new Date(p.expires_at) < new Date())
  );
  const kind = detail.kind || "order";
  const hasDocs = detail.invoice_number || detail.goods_invoice || pods.length > 0;

  return (
    <div>
      <div className="border-2 border-obsidian bg-obsidian text-white px-6 py-5 flex flex-wrap items-center justify-between gap-4 shadow-[6px_6px_0px_0px_#2563EB]" data-testid="tracking-status-banner">
        <div>
          <p className="text-[10px] font-mono font-bold uppercase tracking-[0.25em] text-zinc-400">Current status · {detail.courier_name}</p>
          <p className="font-display font-black text-3xl tracking-tight mt-1 text-sky-300">{statusLabel}</p>
        </div>
        <div className="text-right font-mono text-xs text-zinc-400 space-y-1">
          <p>AWB <span className="text-white font-bold">{detail.awb}</span></p>
          <p>{detail.pickup?.pincode} → {detail.delivery?.pincode}</p>
          {detail.estimated_delivery && <p>ETA {fmtDateTime(detail.estimated_delivery)}</p>}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="border-2 border-obsidian bg-white p-6">
          <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500 flex items-center gap-2">
            <Radar className="w-4 h-4 text-blaze" /> Shipment journey
          </p>
          <div className="mt-6" data-testid="tracking-timeline">
            {steps.map((s, i) => (
              <div key={s.key} className="flex gap-4">
                <div className="flex flex-col items-center">
                  {s.state === "pending"
                    ? <MinusCircle className="w-5 h-5 text-zinc-300 shrink-0" />
                    : s.state === "current"
                      ? <PackageCheck className="w-5 h-5 text-white fill-acid shrink-0" />
                      : <CheckCircle2 className="w-5 h-5 text-obsidian fill-acid shrink-0" />}
                  {i < steps.length - 1 && <div className={`w-0.5 flex-1 min-h-8 ${s.state === "pending" ? "bg-zinc-200" : "bg-obsidian"}`} />}
                </div>
                <div className="pb-7">
                  <p className={`text-sm font-bold ${s.state === "current" ? "text-acid" : s.state === "pending" ? "text-zinc-400" : "text-obsidian"}`} data-testid={`step-${s.key}`}>
                    {s.label}
                    {s.state === "current" && <span className="ml-2 text-[10px] font-mono uppercase tracking-widest bg-acid text-white px-1.5 py-0.5">Now</span>}
                  </p>
                  <p className="text-xs font-mono text-zinc-500 mt-1">
                    {s.event
                      ? `${s.event.location || ""} · ${fmtDateTime(s.event.timestamp)}`
                      : s.state === "pending"
                        ? "Pending"
                        : "Confirmed by courier status"}
                  </p>
                  {s.event?.description && <p className="text-xs text-zinc-500 mt-0.5">{s.event.description}</p>}
                </div>
              </div>
            ))}
            {isTerminalBad && (
              <div className="flex gap-4">
                <MinusCircle className="w-5 h-5 text-red-600 shrink-0" />
                <p className="text-sm font-bold text-red-600">{statusLabel}</p>
              </div>
            )}
          </div>
        </div>

        <div className="border-2 border-obsidian bg-white p-6">
          <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Courier scan history</p>
          <div className="mt-6 max-h-80 overflow-y-auto pr-1" data-testid="tracking-events">
            {events.slice().reverse().map((ev, i) => (
              <div key={i} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <Radar className={`w-4 h-4 shrink-0 ${i === 0 ? "text-acid" : "text-zinc-300"}`} />
                  {i < events.length - 1 && <div className="w-0.5 flex-1 min-h-6 bg-zinc-200" />}
                </div>
                <div className="pb-6">
                  <p className={`text-sm font-semibold ${i === 0 ? "text-obsidian" : "text-zinc-500"}`}>{ev.description || ev.status}</p>
                  <p className="text-xs font-mono text-zinc-500 mt-0.5">{ev.location} · {fmtDateTime(ev.timestamp)}</p>
                </div>
              </div>
            ))}
            {events.length === 0 && (
              <p className="text-sm text-zinc-500">
                Detailed scans not published by the courier yet — the journey shows the courier's confirmed status.
              </p>
            )}
          </div>
          <div className="mt-6 pt-6 border-t-2 border-obsidian grid grid-cols-2 gap-3 text-sm">
            {detail.shipment_type && <div className="flex justify-between"><span className="text-zinc-500">Type</span><span className="font-mono text-xs uppercase">{detail.shipment_type}{detail.mode ? ` · ${detail.mode}` : ""}</span></div>}
            <div className="flex justify-between"><span className="text-zinc-500">Weight</span><span className="font-mono text-xs">{detail.package?.weight_kg ?? "—"} kg</span></div>
            {!publicView && <div className="flex justify-between"><span className="text-zinc-500">Rate</span><span className="font-mono text-xs font-bold">{detail.rate != null ? inr(detail.rate) : "—"}</span></div>}
            <div className="flex justify-between items-center"><span className="text-zinc-500">Status</span><StatusBadge status={detail.status} /></div>
          </div>

          {hasDocs && !publicView && (
            <div className="mt-5 pt-5 border-t border-zinc-200 space-y-2.5" data-testid="tracking-documents">
              <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Documents</p>
              {detail.invoice_number && (
                <p className="text-xs font-mono text-zinc-600" data-testid="tracking-invoice-number">
                  Invoice no: <span className="font-bold text-obsidian">{detail.invoice_number}</span>
                </p>
              )}
              {detail.goods_invoice && (
                <a href={authedFileUrl(`orders/${detail.id}/goods-invoice`)} target="_blank" rel="noreferrer" data-testid="tracking-goods-invoice"
                  className="flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-wider text-acid hover:text-obsidian transition-colors duration-150">
                  <FileText className="w-3.5 h-3.5" /> Goods invoice ↗
                </a>
              )}
              {pods.map((p, i) => (
                <a key={p.id || i} href={authedFileUrl(`pod/${kind}/${detail.id}/${p.id}`)} target="_blank" rel="noreferrer" data-testid={`tracking-pod-${i}`}
                  className="flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-wider text-acid hover:text-obsidian transition-colors duration-150">
                  <FileText className="w-3.5 h-3.5" /> POD {pods.length > 1 ? i + 1 : ""} — proof of delivery ↗
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
